import type { LocalSearchResult } from "./areaSearch";

export const NOMINATIM_ENDPOINT = "https://nominatim.openstreetmap.org/search";

export interface CebuViewbox {
  west: number;
  north: number;
  east: number;
  south: number;
}

export const CEBU_VIEWBOX: CebuViewbox = {
  west: 123.55,
  north: 10.6,
  east: 124.12,
  south: 9.95,
};

export interface SearchOptions {
  endpoint?: string;
  fetcher?: typeof fetch;
  limit?: number;
  signal?: AbortSignal;
  viewbox?: CebuViewbox;
}

export interface NominatimPlace {
  place_id?: number;
  display_name: string;
  lat: string;
  lon: string;
  boundingbox?: string[];
  type?: string;
  class?: string;
}

export interface SearchResult {
  id: string;
  displayName: string;
  lat: number;
  lon: number;
  source: NominatimPlace;
}

export function hasMinimumSearchCharacters(query: string): boolean {
  return query.trim().length >= 3;
}

export function buildNominatimUrl(query: string, options: SearchOptions = {}): string {
  const viewbox = options.viewbox ?? CEBU_VIEWBOX;
  const url = new URL(options.endpoint ?? NOMINATIM_ENDPOINT);
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("q", query.trim());
  url.searchParams.set("limit", String(options.limit ?? 5));
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("countrycodes", "ph");
  url.searchParams.set("viewbox", `${viewbox.west},${viewbox.north},${viewbox.east},${viewbox.south}`);
  url.searchParams.set("bounded", "0");
  return url.toString();
}

export async function searchAddress(query: string, options: SearchOptions = {}): Promise<SearchResult[]> {
  if (!hasMinimumSearchCharacters(query)) {
    return [];
  }

  const fetcher = options.fetcher ?? fetch;
  const response = await fetcher(buildNominatimUrl(query, options), {
    headers: { Accept: "application/json" },
    signal: options.signal,
  });
  if (!response.ok) {
    throw new Error(`Nominatim request failed with ${response.status}`);
  }

  const places = (await response.json()) as NominatimPlace[];
  return places.map((place) => ({
    id: String(place.place_id ?? `${place.lat},${place.lon}`),
    displayName: place.display_name,
    lat: Number(place.lat),
    lon: Number(place.lon),
    source: place,
  }));
}

export interface SearchControllerOptions extends SearchOptions {
  debounceMs?: number;
  minimumCharacters?: number;
  onSelect?: (result: SearchResult) => void;
  localSearch?: (query: string) => LocalSearchResult[];
  onSelectLocal?: (result: LocalSearchResult) => void;
}

export interface SearchController {
  destroy(): void;
}

export function createSearchController(
  input: HTMLInputElement,
  status: HTMLElement,
  results: HTMLElement,
  options: SearchControllerOptions = {},
): SearchController {
  const debounceMs = options.debounceMs ?? 500;
  const minimumCharacters = options.minimumCharacters ?? 3;
  let timer: number | undefined;
  let abortController: AbortController | undefined;

  const searchShell = input.closest(".search-shell");

  results.id = "address-search-results";
  input.setAttribute("aria-controls", "address-search-results");
  input.setAttribute("aria-expanded", "false");

  function resultButtons(): HTMLButtonElement[] {
    return [...results.querySelectorAll<HTMLButtonElement>(".search-result")];
  }

  function clearResults(): void {
    results.textContent = "";
    results.setAttribute("hidden", "");
    input.setAttribute("aria-expanded", "false");
  }

  function setStatus(message: string, state: string): void {
    status.textContent = message;
    status.dataset.state = state;
  }

  function choose(item: SearchResult): void {
    clearResults();
    setStatus(`Showing ${item.displayName}.`, "showing");
    options.onSelect?.(item);
  }

  function chooseLocal(item: LocalSearchResult): void {
    clearResults();
    setStatus(`Showing ${item.name}.`, "showing");
    options.onSelectLocal?.(item);
  }

  function splitDisplayName(displayName: string): { name: string; detail: string } {
    const comma = displayName.indexOf(",");
    if (comma === -1) {
      return { name: displayName, detail: "" };
    }
    return {
      name: displayName.slice(0, comma).trim(),
      detail: displayName.slice(comma + 1).trim(),
    };
  }

  function renderResults(items: SearchResult[]): void {
    results.textContent = "";
    results.removeAttribute("hidden");
    input.setAttribute("aria-expanded", "true");
    const list = document.createElement("ul");
    list.className = "search-results-list";
    list.setAttribute("role", "listbox");

    for (const item of items) {
      const row = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "search-result";
      const { name, detail } = splitDisplayName(item.displayName);
      const nameEl = document.createElement("span");
      nameEl.className = "search-result-name";
      nameEl.textContent = name;
      const detailEl = document.createElement("span");
      detailEl.className = "search-result-detail";
      detailEl.textContent = detail;
      button.appendChild(nameEl);
      button.appendChild(detailEl);
      button.addEventListener("click", () => choose(item));
      row.appendChild(button);
      list.appendChild(row);
    }

    results.appendChild(list);
  }

  function buildAddressRow(item: SearchResult): HTMLLIElement {
    const row = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "search-result";
    const { name, detail } = splitDisplayName(item.displayName);
    const nameEl = document.createElement("span");
    nameEl.className = "search-result-name";
    nameEl.textContent = name;
    const detailEl = document.createElement("span");
    detailEl.className = "search-result-detail";
    detailEl.textContent = detail;
    button.appendChild(nameEl);
    button.appendChild(detailEl);
    button.addEventListener("click", () => choose(item));
    row.appendChild(button);
    return row;
  }

  function buildAreaRow(item: LocalSearchResult): HTMLLIElement {
    const row = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "search-result search-result-area";
    button.dataset.areaKey = item.key;
    button.dataset.tier = item.tier;
    const nameEl = document.createElement("span");
    nameEl.className = "search-result-name";
    nameEl.textContent = item.name;
    const detailEl = document.createElement("span");
    detailEl.className = "search-result-detail";
    detailEl.textContent = item.tierLabel;
    button.appendChild(nameEl);
    button.appendChild(detailEl);
    button.addEventListener("click", () => chooseLocal(item));
    row.appendChild(button);
    return row;
  }

  function buildGroup(labelText: string, groupKey: string, rows: HTMLLIElement[]): HTMLDivElement | null {
    if (rows.length === 0) {
      return null;
    }
    const group = document.createElement("div");
    group.className = "search-group";
    group.dataset.searchGroup = groupKey;
    const label = document.createElement("p");
    label.className = "search-group-label";
    label.id = `search-group-${groupKey}`;
    label.textContent = labelText;
    const list = document.createElement("ul");
    list.className = "search-results-list";
    list.setAttribute("role", "listbox");
    list.setAttribute("aria-labelledby", label.id);
    for (const row of rows) {
      list.appendChild(row);
    }
    group.appendChild(label);
    group.appendChild(list);
    return group;
  }

  function renderGroups(areas: LocalSearchResult[], addresses: SearchResult[]): void {
    const areaGroup = buildGroup("Outage areas", "areas", areas.map(buildAreaRow));
    const addressGroup = buildGroup("Addresses", "addresses", addresses.map(buildAddressRow));
    if (!areaGroup && !addressGroup) {
      clearResults();
      return;
    }
    results.textContent = "";
    results.removeAttribute("hidden");
    input.setAttribute("aria-expanded", "true");
    if (areaGroup) {
      results.appendChild(areaGroup);
    }
    if (addressGroup) {
      results.appendChild(addressGroup);
    }
  }

  function onDocumentPointerDown(event: PointerEvent): void {
    const target = event.target;
    if (target instanceof Node && (searchShell?.contains(target) || input.contains(target) || results.contains(target))) {
      return;
    }
    clearResults();
  }

  function onInputKeyDown(event: KeyboardEvent): void {
    if (event.key === "ArrowDown") {
      const first = resultButtons()[0];
      if (first) {
        event.preventDefault();
        first.focus();
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      clearResults();
      input.focus();
    }
  }

  function onResultsKeyDown(event: KeyboardEvent): void {
    const target = (event.target as HTMLElement).closest<HTMLButtonElement>(".search-result");
    if (!target) {
      return;
    }
    const buttons = resultButtons();
    const index = buttons.indexOf(target);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      buttons[index + 1]?.focus();
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (index > 0) {
        buttons[index - 1]?.focus();
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      clearResults();
      input.focus();
    }
  }

  function queueSearch(): void {
    const query = input.value.trim();
    window.clearTimeout(timer);
    abortController?.abort();
    clearResults();

    if (query.length < minimumCharacters) {
      setStatus(`Type at least ${minimumCharacters} characters to search Cebu. You can also use Locate me.`, "minimum");
      return;
    }

    const localResults = options.localSearch ? options.localSearch(query) : [];
    if (options.localSearch) {
      renderGroups(localResults, []);
    }

    timer = window.setTimeout(() => {
      abortController = new AbortController();
      setStatus("Searching Cebu addresses...", "loading");
      void searchAddress(query, { ...options, signal: abortController.signal })
        .then((items) => {
          if (!options.localSearch) {
            if (items.length === 0) {
              setStatus("No address results. Try Locate me.", "no-results");
              clearResults();
              return;
            }
            setStatus(`${items.length} address result${items.length === 1 ? "" : "s"} found.`, "results");
            renderResults(items);
            return;
          }

          if (items.length === 0) {
            if (localResults.length > 0) {
              setStatus(
                `${localResults.length} matching area${localResults.length === 1 ? "" : "s"}. No address results.`,
                "results",
              );
              renderGroups(localResults, []);
              return;
            }
            setStatus("No address results. Try Locate me.", "no-results");
            clearResults();
            return;
          }

          setStatus(`${items.length} address result${items.length === 1 ? "" : "s"} found.`, "results");
          renderGroups(localResults, items);
        })
        .catch((error: unknown) => {
          if ((error as { name?: string }).name === "AbortError") {
            return;
          }
          if (options.localSearch && localResults.length > 0) {
            renderGroups(localResults, []);
            setStatus("Address search is unavailable. Try Locate me.", "error");
            return;
          }
          setStatus("Address search is unavailable. Try Locate me.", "error");
          clearResults();
        });
    }, debounceMs);
  }

  input.addEventListener("input", queueSearch);
  input.addEventListener("keydown", onInputKeyDown);
  results.addEventListener("keydown", onResultsKeyDown);
  document.addEventListener("pointerdown", onDocumentPointerDown);
  setStatus(`Type at least ${minimumCharacters} characters to search Cebu.`, "idle");

  return {
    destroy() {
      window.clearTimeout(timer);
      abortController?.abort();
      input.removeEventListener("input", queueSearch);
      input.removeEventListener("keydown", onInputKeyDown);
      results.removeEventListener("keydown", onResultsKeyDown);
      document.removeEventListener("pointerdown", onDocumentPointerDown);
    },
  };
}
